import { Breadcrumbs, capitalize, Link } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type React from 'react';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

const fontSize = 16;

const styles = makeStyles((theme: Theme) => ({
  breadCrubmSeparator: {
    color: theme.palette.primary.main,
  },
  breadcrumb: {
    cursor: 'pointer',
    fontSize,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none !important',
    },
  },
  lastBreadCrumb: {
    cursor: 'pointer',
    fontSize,
    color: theme.palette.secondary.main,
    '&:hover': {
      textDecoration: 'none !important',
    },
  },
}));

interface BreadcrumbItem {
  path: string;
  crumb: string;
}

interface CrumbOption {
  crumb: string;
  replaceWith?: string;
  onClick?: (params: { crumb: BreadcrumbItem }) => void;
}

interface ExplicitBreadcrumb {
  label: string;
  onClick: () => void;
}

interface BreadcrumbProps {
  crumbOpts?: CrumbOption[];
  items?: ExplicitBreadcrumb[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ crumbOpts = [], items }) => {
  const classes = styles();
  const navigate = useNavigate();
  // Use TanStack Router's typed search params instead of window.location.search
  const currentSearch = useSearch({ strict: false }) as BaseSearchParams;

  // When explicit items are provided, render them directly — no URL parsing
  if (items) {
    return (
      <Breadcrumbs aria-label="breadcrumb" className={classes.breadCrubmSeparator}>
        {items.map((item, index) => (
          <Link
            key={item.label}
            className={index === items.length - 1 ? classes.lastBreadCrumb : classes.breadcrumb}
            underline="hover"
            color="inherit"
            onClick={item.onClick}
          >
            {item.label}
          </Link>
        ))}
      </Breadcrumbs>
    );
  }

  const isLikelyGlobalID = (segment: string): boolean => {
    try {
      const decoded = atob(decodeURIComponent(segment));
      const split = decoded.split(':');
      if (split.length === 2) {
        return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  const genBreadCrumbs = (urlString: Location): BreadcrumbItem[] => {
    const url = new URL(urlString.href);
    const path = url.pathname;

    const segments = path.split('/').filter((segment) => segment !== '');

    let skipFinal = false;

    const breadCrumbs = segments.map((seg, index) => {
      const breadCrumb: BreadcrumbItem = { path: '', crumb: '' };
      const finalIndex = segments.length - 1;
      const nextIndex = index + 1;

      if ((index === finalIndex && skipFinal) || isLikelyGlobalID(seg)) {
        return breadCrumb;
      }

      if (index === finalIndex) {
        breadCrumb.path = path;
        breadCrumb.crumb = seg;
        if (isLikelyGlobalID(seg)) {
          breadCrumb.crumb = segments[index - 1];
        }
        return breadCrumb;
      }

      const nextSeg = segments[nextIndex];

      if (isLikelyGlobalID(nextSeg)) {
        if (nextIndex === finalIndex) {
          skipFinal = true;
        }
        breadCrumb.path = `/${segments.slice(0, nextIndex + 1).join('/')}`;
        breadCrumb.crumb = seg;
        return breadCrumb;
      }

      breadCrumb.path = `/${seg}`;
      breadCrumb.crumb = seg;

      return breadCrumb;
    });

    return breadCrumbs;
  };

  const breadCrumbs = genBreadCrumbs(window.location).filter((bc) => bc.path && bc.crumb);

  const findCrumb = (crumbName: string): CrumbOption | undefined => {
    return crumbOpts.find(({ crumb }) => crumb === crumbName);
  };

  const _replaceCrumbName = (crumbName: string): string => {
    const crumbOption = findCrumb(crumbName);
    if (crumbOption?.replaceWith) {
      return crumbOption.replaceWith;
    }
    return crumbName;
  };

  const onBreadCrumbClick = (breadcrumb: BreadcrumbItem): void => {
    const crumbOption = findCrumb(breadcrumb?.crumb);
    if (crumbOption?.onClick) {
      crumbOption.onClick({ crumb: breadcrumb });
      return;
    }

    const { sortData } = viewStateVar();

    // Build sort value: use current URL search, then fallback to viewState
    let sortValue = currentSearch.sort;
    if (!sortValue && sortData?.sortBy?.value) {
      sortValue = `${sortData.sortBy.value}:${sortData.orderBy || 'desc'}`;
    }

    // Use TanStack Router's proper navigation pattern with typed search params
    // This avoids URL corruption from manual string concatenation
    navigate({
      to: breadcrumb?.path as '/library',
      search: {
        page: 1, // Reset to page 1 when navigating via breadcrumb
        size: currentSearch.size,
        sort: sortValue,
        term: currentSearch.term,
        filters: currentSearch.filters || [],
        layout: currentSearch.layout,
        scope: currentSearch.scope,
      },
    });
  };

  return (
    <Breadcrumbs aria-label="breadcrumb" className={classes.breadCrubmSeparator}>
      {breadCrumbs.map((breadCrumb, index) => {
        return breadCrumb.crumb ? (
          <Link
            key={breadCrumb.path}
            className={index === breadCrumbs.length - 1 ? classes.lastBreadCrumb : classes.breadcrumb}
            underline="hover"
            color="inherit"
            onClick={() => onBreadCrumbClick(breadCrumb)}
          >
            {capitalize(_replaceCrumbName(breadCrumb.crumb))}
          </Link>
        ) : null;
      })}
    </Breadcrumbs>
  );
};

export default Breadcrumb;
